import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Send, Upload, Play, Pause, RotateCcw,
  Users, Mail, AlertCircle, CheckCircle, Clock,
  ChevronLeft, ChevronRight, Loader2, Zap, BarChart3
} from "lucide-react";

export const OutreachPanel = ({ open, onOpenChange }) => {
  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState({ total_contacts: 0, pending: 0, sent: 0, failed: 0 });
  const [recentSends, setRecentSends] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPages, setContactsPages] = useState(1);
  const [contactsFilter, setContactsFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sendingWave, setSendingWave] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showContacts, setShowContacts] = useState(false);

  useEffect(() => {
    if (open) {
      fetchStatus();
    }
  }, [open]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/outreach/status`);
      if (res.ok) {
        const data = await res.json();
        setCampaign(data.campaign || null);
        setStats(data.stats || { total_contacts: 0, pending: 0, sent: 0, failed: 0 });
        setRecentSends(data.recent_sends || []);
      }
    } catch (err) {
      console.error("Failed to fetch outreach status:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (page = 1, status = "") => {
    try {
      let url = `${API}/outreach/contacts?page=${page}&limit=25`;
      if (status) url += `&status=${status}`;
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        setContactsTotal(data.total || 0);
        setContactsPages(data.pages || 1);
        setContactsPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await authFetch(`${API}/outreach/import`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success(`Imported ${data.imported} contacts. Skipped: ${data.skipped_existing_member} existing, ${data.skipped_duplicate} duplicate, ${data.skipped_unsubscribed} unsubscribed.`);
          fetchStatus();
        }
      } else {
        toast.error("Failed to import contacts");
      }
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleSendTestBatch = async () => {
    setSendingTest(true);
    try {
      const res = await authFetch(`${API}/outreach/send-test-batch`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success(`Test batch sent! ${data.sent} emails sent, ${data.failed} failed. ${data.remaining} remaining.`);
          fetchStatus();
        }
      } else {
        toast.error("Failed to send test batch");
      }
    } catch (err) {
      toast.error("Test batch failed");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendWave = async () => {
    setSendingWave(true);
    try {
      const res = await authFetch(`${API}/outreach/send-wave`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success(`Wave ${data.wave} sent! ${data.sent} emails, ${data.remaining} remaining.`);
          fetchStatus();
        }
      } else {
        toast.error("Failed to send wave");
      }
    } catch (err) {
      toast.error("Wave send failed");
    } finally {
      setSendingWave(false);
    }
  };

  const handleStartAuto = async () => {
    try {
      const res = await authFetch(`${API}/outreach/start-waves`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStatus();
      }
    } catch (err) {
      toast.error("Failed to start auto-waves");
    }
  };

  const handlePause = async () => {
    try {
      const res = await authFetch(`${API}/outreach/pause`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStatus();
      }
    } catch (err) {
      toast.error("Failed to pause campaign");
    }
  };

  const handleResume = async () => {
    try {
      const res = await authFetch(`${API}/outreach/resume`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStatus();
      }
    } catch (err) {
      toast.error("Failed to resume campaign");
    }
  };

  const handleReset = async () => {
    if (!confirm("Are you sure? This will delete ALL imported contacts and campaign data. This cannot be undone.")) return;
    try {
      const res = await authFetch(`${API}/outreach/reset`, { method: "POST" });
      if (res.ok) {
        toast.success("Campaign reset successfully");
        fetchStatus();
        setContacts([]);
      }
    } catch (err) {
      toast.error("Failed to reset campaign");
    }
  };

  const campaignStatus = campaign?.status || "not_started";
  const progress = stats.total_contacts > 0 ? Math.round((stats.sent / stats.total_contacts) * 100) : 0;

  const statusColors = {
    not_started: "bg-neutral-100 text-neutral-600",
    ready: "bg-blue-100 text-blue-700",
    in_progress: "bg-[#39FF14]/15 text-green-700",
    paused: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700"
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="outreach-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-[#39FF14]" />
            Mass Outreach Campaign
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Campaign Status Banner */}
            <div className="rounded-xl border border-neutral-200 p-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-neutral-900" data-testid="outreach-campaign-name">
                    {campaign?.name || "BSC Launch — Subscriber Outreach"}
                  </h3>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    Wave size: 25 contacts | Auto-send every 4 hours
                  </p>
                </div>
                <Badge className={statusColors[campaignStatus] || statusColors.not_started} data-testid="outreach-campaign-status">
                  {campaignStatus === "not_started" ? "Not Started" : campaignStatus.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>

              {/* Progress Bar */}
              {stats.total_contacts > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-neutral-500 mb-1">
                    <span>{stats.sent} of {stats.total_contacts} sent</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#39FF14] rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                      data-testid="outreach-progress-bar"
                    />
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <StatCard icon={Users} label="Total" value={stats.total_contacts} color="text-neutral-600" />
                <StatCard icon={Clock} label="Pending" value={stats.pending} color="text-blue-600" />
                <StatCard icon={CheckCircle} label="Sent" value={stats.sent} color="text-green-600" />
                <StatCard icon={AlertCircle} label="Failed" value={stats.failed} color="text-red-500" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {stats.total_contacts === 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="outreach-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import Subscribers
                </Button>
              )}

              {stats.total_contacts > 0 && stats.pending > 0 && (
                <>
                  <Button
                    onClick={handleSendTestBatch}
                    disabled={sendingTest}
                    variant="outline"
                    className="border-[#39FF14] text-neutral-800 hover:bg-[#39FF14]/10"
                    data-testid="outreach-test-btn"
                  >
                    {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                    Send Test Batch (25)
                  </Button>

                  <Button
                    onClick={handleSendWave}
                    disabled={sendingWave}
                    className="bg-[#39FF14] hover:bg-[#32E010] text-black"
                    data-testid="outreach-send-wave-btn"
                  >
                    {sendingWave ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Next Wave
                  </Button>
                </>
              )}

              {stats.total_contacts > 0 && campaignStatus !== "in_progress" && stats.pending > 0 && (
                <Button
                  onClick={handleStartAuto}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="outreach-start-auto-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Auto-Waves
                </Button>
              )}

              {campaignStatus === "in_progress" && (
                <Button
                  onClick={handlePause}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="outreach-pause-btn"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </Button>
              )}

              {campaignStatus === "paused" && (
                <Button
                  onClick={handleResume}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="outreach-resume-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              )}

              {stats.total_contacts > 0 && (
                <Button
                  onClick={() => { setShowContacts(!showContacts); if (!showContacts) fetchContacts(1, contactsFilter); }}
                  variant="outline"
                  data-testid="outreach-view-contacts-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {showContacts ? "Hide Contacts" : "View Contacts"}
                </Button>
              )}

              {stats.total_contacts > 0 && (
                <Button
                  onClick={handleReset}
                  variant="ghost"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-auto"
                  data-testid="outreach-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
            </div>

            {/* Wave Info */}
            {campaign && (
              <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-50 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Current Wave</span>
                  <span className="font-medium" data-testid="outreach-current-wave">{campaign.current_wave || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Total Sent</span>
                  <span className="font-medium">{campaign.total_sent || 0}</span>
                </div>
                {campaign.last_wave_at && (
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Last Wave</span>
                    <span className="font-medium">{new Date(campaign.last_wave_at).toLocaleString()}</span>
                  </div>
                )}
                {campaign.updated_at && (
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Last Updated</span>
                    <span className="font-medium">{new Date(campaign.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Recent Sends */}
            {recentSends.length > 0 && (
              <div>
                <h4 className="font-medium text-neutral-800 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Recent Sends
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentSends.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-neutral-50">
                      <div>
                        <span className="font-medium text-neutral-800">{c.first_name || "—"}</span>
                        <span className="text-neutral-400 ml-2">{c.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {c.organization && <span className="text-neutral-400">{c.organization}</span>}
                        <Badge variant="outline" className="text-xs">Wave {c.wave}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contacts Browser */}
            {showContacts && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-neutral-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Contact List ({contactsTotal})
                  </h4>
                  <div className="flex gap-1">
                    {["", "pending", "sent", "failed"].map(s => (
                      <Button
                        key={s || "all"}
                        variant={contactsFilter === s ? "default" : "outline"}
                        size="sm"
                        className={contactsFilter === s ? "bg-neutral-900 text-white" : ""}
                        onClick={() => { setContactsFilter(s); fetchContacts(1, s); }}
                        data-testid={`outreach-filter-${s || "all"}`}
                      >
                        {s || "All"}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Name</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Email</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Org</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Wave</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-neutral-50">
                          <td className="py-2 px-3">{c.full_name || c.first_name || "—"}</td>
                          <td className="py-2 px-3 text-neutral-500">{c.email}</td>
                          <td className="py-2 px-3 text-neutral-400 text-xs">{c.organization || "—"}</td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className={
                              c.status === "sent" ? "border-green-300 text-green-700" :
                              c.status === "failed" ? "border-red-300 text-red-600" :
                              "border-neutral-300 text-neutral-600"
                            }>
                              {c.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-neutral-400">{c.wave || "—"}</td>
                        </tr>
                      ))}
                      {contacts.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-neutral-400">No contacts found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {contactsPages > 1 && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-neutral-400">Page {contactsPage} of {contactsPages}</span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contactsPage <= 1}
                        onClick={() => fetchContacts(contactsPage - 1, contactsFilter)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contactsPage >= contactsPages}
                        onClick={() => fetchContacts(contactsPage + 1, contactsFilter)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {stats.total_contacts === 0 && !loading && (
              <div className="text-center py-8 bg-neutral-50 rounded-xl border border-dashed border-neutral-300">
                <Mail className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <h4 className="font-medium text-neutral-700 mb-1">No Contacts Imported Yet</h4>
                <p className="text-sm text-neutral-500 max-w-sm mx-auto">
                  Click "Import Subscribers" to load your 1,500+ subscriber contacts and start the outreach campaign.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-neutral-50 rounded-lg p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <div className="text-lg font-bold text-neutral-900">{value.toLocaleString()}</div>
    <div className="text-xs text-neutral-500">{label}</div>
  </div>
);
